import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";

// Use the same stories as the component
const SUCCESS_STORIES = [
  {
    id: 1,
    title: "From Incarceration to IT Professional",
    clientName: "James M.",
    content: "After 5 years incarcerated, I never thought I'd work in tech. StaffAbility believed in me when no one else would. Now I'm a junior developer with a future I can be proud of.",
    imageUrl: "https://images.unsplash.com/photo-1507537297725-24a1c029d3ca?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    category: "Employment",
    isPublished: true,
    publishDate: "2024-01-15"
  },
  {
    id: 2,
    title: "Recovery to Restaurant Management",
    clientName: "Maria L.",
    content: "After years of addiction, I found StaffAbility when I was ready to change. They helped me with housing, job training, and placed me with an employer who supported my recovery journey.",
    imageUrl: "https://images.unsplash.com/photo-1573497161161-c3e73707e25c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    category: "Recovery",
    isPublished: true,
    publishDate: "2024-02-10"
  },
  {
    id: 3,
    title: "From Homeless to Healthcare Career",
    clientName: "David T.",
    content: "I was living in my car when I found StaffAbility. They helped me complete my GED, get certified as a medical assistant, and find stable housing. I now have a career I love.",
    imageUrl: "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    category: "Housing",
    isPublished: true,
    publishDate: "2024-03-05"
  },
  {
    id: 4,
    title: "Breaking the Cycle of Recidivism",
    clientName: "Robert J.",
    content: "After multiple incarcerations, I found StaffAbility through my parole officer. Their comprehensive support helped me learn coding, find stable housing, and secure a remote tech job that accommodates my parole restrictions.",
    imageUrl: "https://images.unsplash.com/photo-1543269865-cbf427effbad?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    category: "Justice Reform",
    isPublished: true,
    publishDate: "2024-04-10"
  },
  {
    id: 5,
    title: "Single Mother to Software Engineer",
    clientName: "Sarah K.",
    content: "As a single mother with a criminal record, I couldn't find work to support my family. StaffAbility provided childcare assistance during my training and helped me build a career in tech that allows me to work remotely and be there for my children.",
    imageUrl: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    category: "Family Support",
    isPublished: true,
    publishDate: "2024-03-22"
  }
];

export default function SuccessStoriesPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        <div className="bg-primary-50 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-extrabold text-slate-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
                Success Stories
              </h1>
              <p className="max-w-xl mt-5 mx-auto text-xl text-slate-500">
                Real stories of transformation, hope, and new beginnings through the StaffAbility program.
              </p>
            </div>
          </div>
        </div>
        
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Filter by category - placeholder for future functionality */}
            <div className="mb-8 flex flex-wrap gap-2 justify-center">
              <Button variant="outline" className="border-primary-300 bg-primary-50 text-primary-700">
                All Stories
              </Button>
              <Button variant="outline" className="border-slate-300 bg-white text-slate-700">
                Employment
              </Button>
              <Button variant="outline" className="border-slate-300 bg-white text-slate-700">
                Housing
              </Button>
              <Button variant="outline" className="border-slate-300 bg-white text-slate-700">
                Recovery
              </Button>
              <Button variant="outline" className="border-slate-300 bg-white text-slate-700">
                Justice Reform
              </Button>
            </div>
            
            {/* Main stories grid */}
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {SUCCESS_STORIES.map((story) => (
                <div key={story.id} className="bg-white overflow-hidden border border-slate-200 rounded-lg shadow-md flex flex-col h-full hover:shadow-lg transition-shadow duration-300">
                  <div className="relative h-64 bg-gray-200 overflow-hidden">
                    <img 
                      className="absolute inset-0 w-full h-full object-cover" 
                      style={{ objectPosition: '50% 30%' }}
                      src={story.imageUrl} 
                      alt={`Success story of ${story.clientName}`} 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/5"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-lg font-semibold text-white drop-shadow-md">{story.clientName}</h3>
                      <p className="text-sm text-white opacity-90 drop-shadow-md">{story.title}</p>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-white">
                        {story.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex-grow">
                    <div className="mb-4 h-1 w-12 bg-primary-500 rounded"></div>
                    <p className="text-slate-800 mb-4">
                      {story.content.length > 200 ? `${story.content.substring(0, 200)}...` : story.content}
                    </p>
                    <Link href={`/success-stories/${story.id}`}>
                      <span className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer inline-flex items-center">
                        Read full story
                        <svg className="ml-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </span>
                    </Link>
                  </div>
                  <div className="border-t border-slate-100 px-6 py-3 bg-slate-50 text-sm text-slate-700">
                    Published: {new Date(story.publishDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Call to action */}
            <div className="mt-16 bg-primary-50 p-8 rounded-xl text-center shadow-inner">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">Have a Story to Share?</h2>
              <p className="text-slate-600 max-w-2xl mx-auto mb-6">
                If you're a graduate of our program and would like to share your success story to inspire others, we'd love to hear from you.
              </p>
              <Link href="/contact">
                <Button className="bg-primary-600 hover:bg-primary-700 text-white font-medium">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}